import React, {useState} from 'react'
import {Modal, Button, Form, Spinner} from 'react-bootstrap'
import {useDispatch} from 'react-redux'
import {
  changeMobileNumber,
  verifyMobileOtp,
} from '../../../../modules/auth/components/Frontend_Auth_Components/UserLoginRedux/UserLoginSlice'
import {toast} from 'react-toastify'

interface ChangeMobileModalProps {
  show: boolean
  onHide: () => void
  onSuccess: (newMobile: string) => void
  userEmail: string
}

const ChangeMobileModal: React.FC<ChangeMobileModalProps> = ({
  show,
  onHide,
  onSuccess,
  userEmail,
}) => {
  const dispatch = useDispatch()
  const [step, setStep] = useState<'enterMobile' | 'verifyOtp'>('enterMobile')
  const [newMobile, setNewMobile] = useState('')
  const [otp, setOtp] = useState('')
  const [isLoadingSendOtp, setIsLoadingSendOtp] = useState(false)
  const [isLoadingVerifyOtp, setIsLoadingVerifyOtp] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)

  // Phone number validation function
  const validatePhoneNumber = (phone: string): boolean => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '')

    // Check if it's a valid phone number (8-15 digits)
    if (cleanPhone.length < 8 || cleanPhone.length > 15) {
      return false
    }

    // Additional validation: must start with a digit and not be all zeros
    if (cleanPhone === '0'.repeat(cleanPhone.length)) {
      return false
    }

    return true
  }

  // Countdown timer function
  const startCountdown = () => {
    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleClose = () => {
    setStep('enterMobile')
    setNewMobile('')
    setOtp('')
    setResendCountdown(0)
    onHide()
  }

  const handleSendOtp = async () => {
    if (!newMobile || newMobile.trim() === '') {
      toast.error('Please enter a mobile number')
      return
    }

    if (!validatePhoneNumber(newMobile)) {
      toast.error('Please enter a valid mobile number (8-15 digits)')
      return
    }

    setIsLoadingSendOtp(true)
    try {
      const result = await dispatch(
        changeMobileNumber({
          email: userEmail,
          newMobile: newMobile,
        })
      )

      if (result.payload?.status) {
        setStep('verifyOtp')
        setResendCountdown(60) // Start 60-second countdown
        startCountdown()
      } else {
        toast.error(result.payload?.message || 'Failed to send OTP')
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send OTP')
    } finally {
      setIsLoadingSendOtp(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (!otp || otp.trim() === '') {
      toast.error('Please enter OTP')
      return
    }

    setIsLoadingVerifyOtp(true)
    try {
      const result = await dispatch(
        verifyMobileOtp({
          email: userEmail,
          otp: otp,
          type: 'CHANGE_MOBILE',
        })
      )

      if (result.payload?.status) {
        toast.success('OTP verified successfully! Your phone number had been changed.')
        onSuccess(newMobile)
        handleClose()
      } else {
        toast.error(result.payload?.message || 'Invalid OTP')
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to verify OTP')
    } finally {
      setIsLoadingVerifyOtp(false)
    }
  }

  const handleResendOtp = async () => {
    if (resendCountdown > 0) {
      return // Don't allow resend if countdown is active
    }

    setOtp('')
    setIsLoadingSendOtp(true)
    try {
      const result = await dispatch(
        changeMobileNumber({
          email: userEmail,
          newMobile: newMobile,
        })
      )

      if (result.payload?.status) {
        setResendCountdown(60) // Start 60-second countdown
        startCountdown()
        toast.success('OTP sent successfully!')
      } else {
        toast.error(result.payload?.message || 'Failed to resend OTP')
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to resend OTP')
    } finally {
      setIsLoadingSendOtp(false)
    }
  }

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {step === 'enterMobile' ? 'Change Mobile Number' : 'Verify OTP for New Mobile'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 'enterMobile' ? (
          <div>
            <Form.Group className='mb-4'>
              <Form.Label className='fw-bold fs-6'>New Mobile Number</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter new mobile number (8-15 digits)'
                value={newMobile}
                onChange={(e) => {
                  // Allow only digits, spaces, hyphens, and parentheses
                  const value = e.target.value.replace(/[^\d\s\-()]/g, '')
                  setNewMobile(value)
                }}
                className='form-control-lg form-control-solid'
              />
              <Form.Text className='text-muted'>
                Enter your new mobile number. We'll send you an OTP to verify it.
              </Form.Text>
            </Form.Group>
          </div>
        ) : (
          <div>
            <div className='mb-4'>
              <p className='text-muted mb-2'>
                We've sent an OTP to <strong>{newMobile}</strong> to verify this mobile number.
              </p>
            </div>
            <Form.Group className='mb-4'>
              <Form.Label className='fw-bold fs-6'>Enter OTP</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter 6-digit OTP'
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                className='form-control-lg form-control-solid'
              />
            </Form.Group>
            <div className='text-center'>
              <button
                type='button'
                className='btn btn-link text-primary p-0'
                onClick={handleResendOtp}
                disabled={isLoadingSendOtp || resendCountdown > 0}
              >
                {isLoadingSendOtp
                  ? 'Resending...'
                  : resendCountdown > 0
                  ? `Resend OTP in ${resendCountdown}s`
                  : "Didn't receive OTP? Resend"}
              </button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='light'
          onClick={handleClose}
          disabled={isLoadingSendOtp || isLoadingVerifyOtp}
        >
          Cancel
        </Button>
        {step === 'enterMobile' ? (
          <Button
            variant='primary'
            onClick={handleSendOtp}
            disabled={isLoadingSendOtp || !newMobile}
          >
            {isLoadingSendOtp ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                Sending...
              </>
            ) : (
              'Send OTP'
            )}
          </Button>
        ) : (
          <Button variant='primary' onClick={handleVerifyOtp} disabled={isLoadingVerifyOtp || !otp}>
            {isLoadingVerifyOtp ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                Verifying...
              </>
            ) : (
              'Verify & Update Mobile'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default ChangeMobileModal
